import { useState, useEffect, useCallback, useMemo } from "react";
import * as Icons from "lucide-react";
import { api } from "@/lib/api";
import RequestedServicesEditor from "@/components/workspace/RequestedServicesEditor";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { COUNTRIES, statesForCountry } from "@/lib/addressData";
import { useAuth } from "@/context/AuthContext";

const STATUS_META = {
  staff_verified: { label: "Verified", dot: "bg-fuchsia-500", text: "text-fuchsia-600", icon: "BadgeCheck" },
  answered: { label: "Answered", dot: "bg-emerald-500", text: "text-emerald-600", icon: "CheckCircle2" },
  needs_followup: { label: "Needs Follow-Up", dot: "bg-amber-500", text: "text-amber-600", icon: "Clock" },
  unknown: { label: "Unknown", dot: "bg-slate-300", text: "text-slate-500", icon: "HelpCircle" },
  pending_client: { label: "Pending Client", dot: "bg-blue-500", text: "text-blue-600", icon: "UserClock" },
  not_applicable: { label: "Not Applicable", dot: "bg-slate-700", text: "text-slate-600", icon: "MinusCircle" },
};
const STATUS_ORDER = ["staff_verified", "answered", "needs_followup", "pending_client", "unknown", "not_applicable"];
const SOURCE_META = {
  staff: { label: "Staff", icon: "UserCog" }, portal: { label: "Portal", icon: "Globe2" },
  organizer: { label: "Organizer", icon: "ClipboardList" }, penny: { label: "Penny", icon: "Bot" },
  allison: { label: "Allison", icon: "Sparkles" }, imported: { label: "Imported", icon: "Download" },
};
const fmtDT = (d) => (d ? new Date(d).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—");
const cap = (s) => (s || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
const ADDRESS_SUBFIELDS = [["address_1", "Address 1"], ["address_2", "Address 2"], ["city", "City"], ["state", "State / Province"], ["postal_code", "Postal Code"], ["country", "Country"]];
const fmtAddr = (v) => [v.address_1, v.address_2, v.city, v.state, v.postal_code, v.country].filter(Boolean).join(", ");
const fmtVal = (v) => (v && typeof v === "object") ? fmtAddr(v) : String(v ?? "");

// Canonical visa option values — used for presentation-only "Other:" detection.
// Stored values are never modified; only the staff display label is prefixed.
const CANONICAL_VISA_VALUES = new Set([
  "DTV", "Non-Immigrant O", "Non-Immigrant O-A", "Non-Immigrant O-X",
  "Non-Immigrant B", "Non-Immigrant ED", "Non-Immigrant F", "Non-Immigrant M",
  "Non-Immigrant R", "Non-Immigrant RS", "Non-Immigrant IB", "SMART Visa",
  "LTR", "Thailand Privilege", "Permanent Resident", "Visa Exemption",
  "Visa on Arrival", "Tourist Visa",
]);
const fmtVisaVal = (v) => {
  if (!v) return v;
  return CANONICAL_VISA_VALUES.has(v) ? v : `Other: ${v}`;
};

/* ---- completion donut ---- */
function Donut({ percent, label = "Complete", size = 130 }) {
  const r = (size - 16) / 2, C = 2 * Math.PI * r;
  return (
    <div className="relative inline-flex items-center justify-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} strokeWidth="10" className="stroke-slate-100" fill="none" />
        <circle cx={size / 2} cy={size / 2} r={r} strokeWidth="10" strokeLinecap="round" fill="none"
          className="stroke-blue-600 transition-all duration-700" strokeDasharray={C} strokeDashoffset={C - (C * percent) / 100} />
      </svg>
      <div className="absolute text-center">
        <div className="text-2xl font-heading font-bold text-[#171B2E]">{percent}%</div>
        <div className="text-[11px] text-slate-400">{label}</div>
      </div>
    </div>
  );
}

/* ---- status badge with change popover ---- */
function StatusBadge({ status, onChange, disabled }) {
  const [open, setOpen] = useState(false);
  const meta = STATUS_META[status];
  if (!meta) {
    return (
      <button disabled={disabled} onClick={() => !disabled && onChange && setOpen(true)}
        className="text-[11px] text-slate-300 hover:text-slate-500" data-testid="tp-status-blank">— set status</button>
    );
  }
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button disabled={disabled} data-testid="tp-status-badge"
          className={cn("inline-flex items-center gap-1.5 text-[11px] font-medium", meta.text, disabled && "opacity-70")}>
          <span className={cn("h-2 w-2 rounded-full", meta.dot)} />{meta.label}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-52 p-1.5" align="end">
        <div className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400">Change status</div>
        {STATUS_ORDER.map((s) => {
          const m = STATUS_META[s];
          return (
            <button key={s} data-testid={`tp-status-opt-${s}`}
              onClick={() => { onChange(s); setOpen(false); }}
              className={cn("w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-sm hover:bg-slate-50",
                s === status && "bg-slate-50")}>
              <span className={cn("h-2 w-2 rounded-full", m.dot)} />{m.label}
              {s === status && <Icons.Check className="h-3.5 w-3.5 ml-auto text-slate-400" />}
            </button>
          );
        })}
      </PopoverContent>
    </Popover>
  );
}

/* ---- single editable field ---- */
function ProfileField({ field, onValue, onStatus, primaryAddress, onSyncPrimary }) {
  const { type, options, value, status, effective_status, source, updated_at, updated_by_name, label, required } = field;
  // R3 (CIE-2): Use backend-supplied effective_status for display when present.
  // When effective_status is explicitly null (blank+answered), the badge renders
  // nothing — do NOT fall back to status.  Only fall back when the key is absent.
  const displayStatus = "effective_status" in field ? effective_status : status;
  const [sameAsPrimary, setSameAsPrimary] = useState(false);
  const isMailing = field.key === "mailing_address" && primaryAddress !== undefined;

  // When "Same as Legal Tax Residence" is on, mirror the Mailing Address INTO the
  // Legal Tax Residence (Primary Address) so both stay identical.
  useEffect(() => {
    if (sameAsPrimary && onSyncPrimary && value && typeof value === "object") onSyncPrimary({ ...value });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sameAsPrimary, JSON.stringify(value)]);

  const control = () => {
    if (type === "address") {
      const addr = (value && typeof value === "object") ? value : (typeof value === "string" && value ? { address_1: value } : {});
      const setPart = (k, v) => onValue({ ...addr, [k]: v });
      const states = statesForCountry(addr.country);
      const listId = `dl-${field.key}`;
      const disabled = false;  // Mailing stays editable — it is the source when synced to Legal
      return (
        <div className="space-y-2">
          {isMailing && (
            <label className="flex items-center gap-2 text-xs text-slate-500 cursor-pointer" data-testid="tp-same-as-primary">
              <input type="checkbox" checked={sameAsPrimary} onChange={(e) => setSameAsPrimary(e.target.checked)} className="rounded border-slate-300 text-brand-gold-dark focus:ring-brand-gold/40" />
              Same as Legal Tax Residence
            </label>
          )}
          <div className={cn("grid grid-cols-2 gap-2", disabled && "opacity-60")}>
            <datalist id={listId}>{COUNTRIES.map((c) => <option key={c} value={c} />)}</datalist>
            {states && <datalist id={`${listId}-st`}>{states.map((s) => <option key={s} value={s} />)}</datalist>}
            {ADDRESS_SUBFIELDS.map(([k, lbl]) => (
              <input key={k} data-testid={`tp-field-${field.key}-${k}`} value={addr[k] || ""} placeholder={lbl}
                disabled={disabled}
                list={k === "country" ? listId : (k === "state" && states ? `${listId}-st` : undefined)}
                onChange={(e) => setPart(k, e.target.value)}
                className={cn("text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-200 disabled:bg-slate-50",
                  (k === "address_1" || k === "address_2") && "col-span-2")} />
            ))}
          </div>
        </div>
      );
    }
    if (type === "textarea")
      return <textarea data-testid={`tp-field-${field.key}`} value={value || ""} onChange={(e) => onValue(e.target.value)} rows={2}
        className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-200 resize-y" />;
    if (type === "text")
      return (
        <>
          <input data-testid={`tp-field-${field.key}`} value={value || ""} onChange={(e) => onValue(e.target.value)}
            className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-200" />
          {field.key === "thai_visa_type" && value && !CANONICAL_VISA_VALUES.has(value) && (
            <p className="text-[10px] text-amber-600 mt-0.5" data-testid="visa-other-annotation">
              Other: stored as free text — not a canonical visa type
            </p>
          )}
        </>
      );
    return (
      <select data-testid={`tp-field-${field.key}`} value={value || ""} onChange={(e) => onValue(e.target.value)}
        className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-blue-200">
        <option value="">Select…</option>
        {(options || []).map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  };
  const srcMeta = source ? SOURCE_META[source] : null;
  return (
    <div className="space-y-1.5" data-testid={`tp-fieldrow-${field.key}`}>
      <div className="flex items-center justify-between gap-2">
        <label className="text-xs font-medium text-slate-500">{label}{required && <span className="text-rose-400 ml-0.5">*</span>}</label>
        <StatusBadge status={displayStatus} onChange={onStatus} />
      </div>
      {control()}
      {(srcMeta || updated_at) && (
        <div className="flex items-center gap-1.5 text-[10px] text-slate-400" title={`Updated ${fmtDT(updated_at)}${updated_by_name ? ` by ${updated_by_name}` : ""}`}>
          {srcMeta && <>{Icons[srcMeta.icon] && (() => { const I = Icons[srcMeta.icon]; return <I className="h-3 w-3" />; })()}<span>{srcMeta.label}</span></>}
          {updated_at && <span className="text-slate-300">· {fmtDT(updated_at)}</span>}
        </div>
      )}
    </div>
  );
}

/* ---- computed (read-only) field ---- */
function ComputedField({ label, value }) {
  const isYes = value === "Yes"; const isNo = value === "No";
  const tone = isYes ? "text-emerald-600" : isNo ? "text-slate-400" : "text-[#171B2E]";
  return (
    <div className="rounded-xl border border-slate-100 bg-slate-50/60 px-3 py-2.5">
      <div className="text-[10px] uppercase tracking-wide text-slate-400">{label}</div>
      <div className={cn("text-sm font-semibold mt-0.5 capitalize", tone)}>{cap(String(value))}</div>
    </div>
  );
}

/* ---- section card ---- */
const RS_LEGACY_KEYS = ["requested_service", "service_category", "service_year"];
function SectionCard({ section, onValue, onStatus, idx, serviceOptions, requestedServices, onRequestedServices }) {
  const [open, setOpen] = useState(true);
  const Icon = Icons[section.icon] || Icons.FileText;
  const pct = section.progress?.percent;
  const done = pct === 100;
  const isRequestedServices = section.key === "requested_service";
  const sharedFields = isRequestedServices ? section.fields.filter((f) => !RS_LEGACY_KEYS.includes(f.key)) : section.fields;
  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm" data-testid={`tp-section-${section.key}`}>
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center gap-3 p-4">
        <span className="h-7 w-7 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center shrink-0">{idx}</span>
        <Icon className="h-5 w-5 text-slate-400" />
        <h3 className="font-heading text-base font-semibold text-[#171B2E]">{section.title}</h3>
        <div className="ml-auto flex items-center gap-3">
          {pct != null && (
            <div className="hidden sm:flex items-center gap-2">
              <div className="w-20 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                <div className={cn("h-full rounded-full", done ? "bg-emerald-500" : pct >= 50 ? "bg-blue-500" : "bg-amber-400")} style={{ width: `${pct}%` }} />
              </div>
              <span className="text-xs text-slate-400 w-9 text-right">{pct}%</span>
            </div>
          )}
          {done && <span className="hidden md:inline-flex items-center gap-1 text-xs text-emerald-600 font-medium"><Icons.CheckCircle2 className="h-4 w-4" />Complete</span>}
          <Icons.ChevronDown className={cn("h-4 w-4 text-slate-400 transition-transform", open && "rotate-180")} />
        </div>
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-4">
          {isRequestedServices && (
            <RequestedServicesEditor items={requestedServices} options={serviceOptions} onChange={onRequestedServices} />
          )}
          {section.computed ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2.5">
              {sharedFields.map((f) => <ComputedField key={f.key} label={f.label} value={f.value} />)}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-4">
              {sharedFields.map((f) => (
                <ProfileField key={f.key} field={f}
                  primaryAddress={f.key === "mailing_address" ? (section.fields.find((x) => x.key === "legal_tax_residence") || {}).value : undefined}
                  onSyncPrimary={f.key === "mailing_address" ? ((v) => onValue(section.key, "legal_tax_residence", v)) : undefined}
                  onValue={(v) => onValue(section.key, f.key, v)}
                  onStatus={(s) => onStatus(section.key, f.key, s)} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const PRU_SOURCE = {
  portal: { label: "Client Portal", icon: "Globe2" }, meeting: { label: "Meeting", icon: "Mic" },
  voicemail: { label: "Voicemail", icon: "Phone" }, email: { label: "Email", icon: "Mail" },
  organizer: { label: "Organizer", icon: "ClipboardList" }, web_chat: { label: "Web Chat", icon: "MessageCircle" },
  document: { label: "Document", icon: "FileText" }, manual: { label: "Manual", icon: "UserCog" },
};

/* ---- Proposed Changes review surface (Package 4) — channel-neutral ---- */
function ProposedChanges({ clientId, onResolved }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(null);
  const [edit, setEdit] = useState({});

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/proposed-record-updates`, { params: { client_id: clientId, status: "pending" } });
      setItems(data.items || []);
    } catch (e) { console.warn("Failed to load proposed record updates", e); }
  }, [clientId]);
  useEffect(() => { load(); }, [load]);

  const resolve = async (p, mode) => {
    setBusy(p.id);
    try {
      if (mode === "reject") await api.post(`/proposed-record-updates/${p.id}/reject`, {});
      else if (mode === "edit") await api.post(`/proposed-record-updates/${p.id}/accept-with-edit`, { value: edit[p.id] ?? p.proposed_value, acknowledge_conflict: true });
      else await api.post(`/proposed-record-updates/${p.id}/accept`, { acknowledge_conflict: true });
      toast.success(mode === "reject" ? "Proposal rejected" : "Change applied");
      await load(); onResolved && onResolved();
    } catch (e) {
      toast.error(e?.response?.status === 409 ? "Record changed — refresh and retry" : "Could not resolve");
    } finally { setBusy(null); }
  };

  if (!items.length) return null;
  return (
    <>
      <div className="rounded-2xl border border-blue-300/50 bg-gradient-to-br from-blue-50 to-white p-4 flex items-center gap-3" data-testid="proposed-changes-banner">
        <Icons.GitPullRequestArrow className="h-5 w-5 text-blue-500 shrink-0" />
        <div className="flex-1">
          <div className="font-semibold text-[#171B2E] text-sm">Proposed Changes</div>
          <div className="text-xs text-slate-500">{items.length} change{items.length > 1 ? "s" : ""} awaiting your review</div>
        </div>
        <button onClick={() => setOpen(true)} data-testid="proposed-changes-review-btn" className="text-sm font-semibold text-blue-700 hover:text-blue-800 whitespace-nowrap">Review</button>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="proposed-changes-drawer">
          <DialogHeader><DialogTitle>Proposed Changes ({items.length})</DialogTitle>
            <DialogDescription>Client- and meeting-submitted changes awaiting approval.</DialogDescription></DialogHeader>
          <div className="space-y-3 max-h-[65vh] overflow-y-auto">
            {items.map((p) => {
              const src = PRU_SOURCE[p.source?.channel] || { label: p.source?.channel, icon: "Circle" };
              const SI = Icons[src.icon] || Icons.Circle;
              return (
                <div key={p.id} className="border border-slate-200 rounded-xl p-3" data-testid={`proposed-change-${p.target_field}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-medium text-sm text-[#171B2E]">{p.label}</div>
                    <span className="inline-flex items-center gap-1 text-[10px] font-medium text-slate-500 bg-slate-100 rounded-full px-2 py-0.5"><SI className="h-3 w-3" />{src.label}</span>
                  </div>
                  <div className="mt-1.5 text-xs text-slate-500">
                    <span className="line-through text-slate-400">{fmtVal(p.current_value) || "—"}</span> → <span className="font-semibold text-[#171B2E]">{p.target_field === "thai_visa_type" ? fmtVisaVal(fmtVal(p.proposed_value)) : fmtVal(p.proposed_value)}</span>
                  </div>
                  {p.basis?.client_comment && <div className="mt-1 text-[11px] text-slate-500 italic">“{p.basis.client_comment}”</div>}
                  <div className="text-[10px] text-slate-400 mt-1">{fmtDT(p.created_at)}{p.tax_year ? ` · Tax year ${p.tax_year}` : ""}</div>
                  <div className="mt-2.5 flex items-center gap-2">
                    <input value={edit[p.id] ?? ""} placeholder="Edit value (optional)" onChange={(e) => setEdit((s) => ({ ...s, [p.id]: e.target.value }))}
                      data-testid={`proposed-change-edit-${p.target_field}`} className="flex-1 text-xs border border-slate-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-200" />
                  </div>
                  <div className="mt-2 flex items-center gap-2">
                    <button disabled={busy === p.id} onClick={() => resolve(p, "accept")} data-testid={`proposed-accept-${p.target_field}`} className="inline-flex items-center gap-1 h-8 px-3 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold disabled:opacity-60"><Icons.Check className="h-3.5 w-3.5" />Accept</button>
                    <button disabled={busy === p.id || !(edit[p.id] ?? "").trim()} onClick={() => resolve(p, "edit")} data-testid={`proposed-edit-${p.target_field}`} className="inline-flex items-center gap-1 h-8 px-3 rounded-lg border border-slate-200 text-slate-700 text-xs font-semibold hover:bg-slate-50 disabled:opacity-40"><Icons.Pencil className="h-3.5 w-3.5" />Edit &amp; Accept</button>
                    <button disabled={busy === p.id} onClick={() => resolve(p, "reject")} data-testid={`proposed-reject-${p.target_field}`} className="inline-flex items-center gap-1 h-8 px-3 rounded-lg border border-rose-200 text-rose-600 text-xs font-semibold hover:bg-rose-50 disabled:opacity-60"><Icons.X className="h-3.5 w-3.5" />Reject</button>
                  </div>
                </div>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default function TaxProfilePanel({ clientId }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "administrator";
  const [profile, setProfile] = useState(null);
  const [serviceOptions, setServiceOptions] = useState(null);
  const [dirty, setDirty] = useState({});
  const [saving, setSaving] = useState(false);
  const [missing, setMissing] = useState([]);
  const [showMissing, setShowMissing] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState([]);
  const [allison, setAllison] = useState(null);
  const [askingAllison, setAskingAllison] = useState(false);
  const [p1Running, setP1Running] = useState(false);
  const [p1Result, setP1Result] = useState(null);
  const [showP1Dialog, setShowP1Dialog] = useState(false);

  // Stage 3: CIE Conflict Review
  const [conflicts, setConflicts] = useState([]);
  const [conflictsLoading, setConflictsLoading] = useState(false);
  const [conflictsBusy, setConflictsBusy] = useState(null); // conflict_id being processed
  const [showConflictDetail, setShowConflictDetail] = useState(null); // conflict_id for detail view
  const [ackModal, setAckModal] = useState(null); // { conflict, action } pending professional-review ack

  const load = useCallback(async () => {
    try {
      const [p, m] = await Promise.all([
        api.get(`/tax-profile/${clientId}`),
        api.get(`/tax-profile/${clientId}/missing`),
      ]);
      setProfile(p.data); setMissing(m.data.items || []); setDirty({});
    } catch { toast.error("Could not load Tax Profile"); }
  }, [clientId]);

  const loadConflicts = useCallback(async () => {
    if (!isAdmin) return;
    setConflictsLoading(true);
    try {
      const { data } = await api.get(`/admin/cie/conflicts/${clientId}?include_resolved=false`);
      setConflicts(data.conflicts || []);
    } catch (e) {
      console.warn("Could not load CIE conflicts", e);
    } finally { setConflictsLoading(false); }
  }, [clientId, isAdmin]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (isAdmin) loadConflicts(); }, [loadConflicts, isAdmin]);
  useEffect(() => {
    api.get(`/tax-profile/service-options`).then(({ data }) => setServiceOptions(data))
      .catch((e) => console.warn("Failed to load service options", e));
  }, []);

  const onRequestedServices = (items) => {
    setDirty((d) => ({ ...d, requested_services: { field_key: "requested_services", value: items, status: items.length ? "answered" : "unknown" } }));
  };

  const setFieldLocal = (sectionKey, key, patch) => {
    setProfile((prev) => {
      if (!prev) return prev;
      const sections = prev.sections.map((s) => s.key !== sectionKey ? s
        : { ...s, fields: s.fields.map((f) => f.key === key ? { ...f, ...patch } : f) });
      return { ...prev, sections };
    });
    setDirty((d) => ({ ...d, [key]: { ...(d[key] || { field_key: key }), ...patch } }));
  };
  const onValue = (sk, key, value) => setFieldLocal(sk, key, { value, status: "answered" });
  const onStatus = (sk, key, status) => setFieldLocal(sk, key, { status });

  const save = async () => {
    const updates = Object.values(dirty).map((u) => ({ field_key: u.field_key, value: u.value, status: u.status, source: "staff" }));
    if (!updates.length) { toast.info("No changes to save"); return; }
    setSaving(true);
    try {
      const { data } = await api.patch(`/tax-profile/${clientId}`, { updates });
      setProfile(data); setDirty({});
      const m = await api.get(`/tax-profile/${clientId}/missing`); setMissing(m.data.items || []);
      toast.success("Tax profile saved");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.error === "requested_services_invalid" && detail?.fields?.length) {
        const msgs = detail.fields
          .map((f) => `Row ${(f.index ?? 0) + 1}: ${f.message}`)
          .join(" · ");
        toast.error(msgs, { duration: 8000 });
      } else {
        toast.error("Save failed");
      }
    } finally { setSaving(false); }
  };

  const openHistory = async () => {
    setShowHistory(true);
    try { const { data } = await api.get(`/tax-profile/${clientId}/history`); setHistory(data.items || []); } catch (e) { console.warn("Failed to load tax profile history", e); }
  };
  const askAllison = async () => {
    setAskingAllison(true);
    try { const { data } = await api.post(`/tax-profile/${clientId}/allison`); setAllison(data); }
    catch { toast.error("Allison is unavailable"); } finally { setAskingAllison(false); }
  };

  const runP1Apply = async () => {
    setP1Running(true);
    try {
      const since = new Date(Date.now() - 365 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
      const { data } = await api.post("/admin/cie/p1-apply", {
        client_id: clientId,
        since_date: since,
      });
      setP1Result(data);
      setShowP1Dialog(true);
      if (data.applied_count > 0) {
        toast.success(`Allison applied ${data.applied_count} field${data.applied_count > 1 ? "s" : ""}`);
        load();
      } else {
        toast.info("No new eligible fields found to populate");
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(detail || "P1 apply failed");
    } finally { setP1Running(false); }
  };

  const decideConflict = async (conflict, action, { reason, professional_review_acknowledged } = {}) => {
    setConflictsBusy(conflict.id);
    try {
      await api.post(`/admin/cie/conflicts/${conflict.id}/decide`, {
        action,
        reason: reason || null,
        professional_review_acknowledged: !!professional_review_acknowledged,
      });
      const labels = { accept: "accepted", reject: "rejected", defer: "deferred" };
      toast.success(`Conflict ${labels[action] || action}`);
      loadConflicts();
      if (action === "accept") load(); // reload tax profile to reflect applied value
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.message) {
        toast.error(detail.message);
      } else if (e?.response?.status === 409) {
        toast.warning("Conflict is stale — canonical value changed. Refreshed conflict list.");
        loadConflicts();
      } else {
        toast.error(typeof detail === "string" ? detail : "Decision failed");
      }
    } finally {
      setConflictsBusy(null);
      setAckModal(null);
    }
  };

  const handleConflictAction = (conflict, action) => {
    const isHighLegal = ["high_risk", "legal_conclusion"].includes(conflict.review_reason);
    if (action === "accept" && isHighLegal) {
      setAckModal({ conflict, action });
      return;
    }
    decideConflict(conflict, action);
  };

  const scores = profile?.scores;
  const counts = scores?.counts || {};
  const summary = profile?.summary || {};
  const dirtyCount = Object.keys(dirty).length;

  const summaryChips = useMemo(() => ([
    { label: "Risk Level", value: cap(summary.risk_level), tone: summary.risk_level === "high" ? "text-rose-600" : summary.risk_level === "moderate" ? "text-amber-600" : "text-emerald-600" },
    { label: "Estimated Complexity", value: cap(summary.estimated_complexity), tone: "text-amber-600" },
    { label: "Filing Status", value: summary.filing_status || "—", tone: "text-[#171B2E]" },
    { label: "Tax Year", value: summary.service_year || "—", tone: "text-[#171B2E]" },
    { label: "Streamlined Candidate", value: summary.streamlined_candidate ? "Yes" : "No", tone: summary.streamlined_candidate ? "text-emerald-600" : "text-slate-500" },
  ]), [summary]);

  if (!profile) return <div className="flex justify-center py-16"><Icons.Loader2 className="h-6 w-6 animate-spin text-blue-500" /></div>;

  return (
    <div className="space-y-5" data-testid="tax-profile-panel">
      {/* header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-heading text-xl font-bold text-[#171B2E]">Tax Profile</h2>
          <p className="text-xs text-slate-400">The master tax record · Last updated {fmtDT(profile.updated_at)}</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={openHistory} data-testid="tp-history-btn" className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-slate-200 text-sm text-slate-600 hover:bg-slate-50"><Icons.History className="h-4 w-4" />History</button>
          <button onClick={save} disabled={saving} data-testid="tp-save-btn" className="inline-flex items-center gap-1.5 h-9 px-4 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold disabled:opacity-60">
            {saving ? <Icons.Loader2 className="h-4 w-4 animate-spin" /> : <Icons.Save className="h-4 w-4" />}Save Changes{dirtyCount ? ` (${dirtyCount})` : ""}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* main column */}
        <div className="lg:col-span-2 space-y-4">
          <ProposedChanges clientId={clientId} onResolved={load} />
          {/* profile summary */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-3">Profile Summary</div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2.5">
              {summaryChips.map((c) => (
                <div key={c.label} className="rounded-xl border border-slate-100 px-3 py-2">
                  <div className="text-[10px] uppercase tracking-wide text-slate-400">{c.label}</div>
                  <div className={cn("text-sm font-semibold mt-0.5", c.tone)}>{c.value}</div>
                </div>
              ))}
            </div>
          </div>

          {/* action needed */}
          {missing.length > 0 && (
            <div className="rounded-2xl border border-amber-300/50 bg-gradient-to-br from-amber-50 to-white p-4 flex items-center gap-3" data-testid="tp-action-needed">
              <Icons.Lightbulb className="h-5 w-5 text-amber-500 shrink-0" />
              <div className="flex-1">
                <div className="font-semibold text-[#171B2E] text-sm">Action Needed</div>
                <div className="text-xs text-slate-500">{missing.length} item{missing.length > 1 ? "s" : ""} need attention to reach 100% profile completion.</div>
              </div>
              <button onClick={() => setShowMissing(true)} data-testid="tp-view-missing" className="text-sm font-semibold text-amber-700 hover:text-amber-800 whitespace-nowrap">View Missing Items</button>
            </div>
          )}

          {/* sections */}
          {profile.sections.map((s, i) => (
            <SectionCard key={s.key} section={s} idx={i + 1} onValue={onValue} onStatus={onStatus}
              serviceOptions={serviceOptions} requestedServices={profile.requested_services || []}
              onRequestedServices={onRequestedServices} />
          ))}

          {/* legend */}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-[11px] text-slate-500 px-1" data-testid="tp-legend">
            <span className="font-semibold text-slate-400">Status:</span>
            {STATUS_ORDER.map((s) => (
              <span key={s} className="inline-flex items-center gap-1.5"><span className={cn("h-2 w-2 rounded-full", STATUS_META[s].dot)} />{STATUS_META[s].label}</span>
            ))}
          </div>
        </div>

        {/* sidebar */}
        <div className="space-y-4">
          {/* overview */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 text-center" data-testid="tp-overview">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-2 text-left">Profile Overview</div>
            <Donut percent={scores.completion} />
            <div className="mt-3 flex items-center justify-center gap-2 text-xs">
              <span className="text-slate-400">Confidence</span>
              <span className="font-semibold text-fuchsia-600" data-testid="tp-confidence">{scores.confidence}% verified</span>
            </div>
            <div className="mt-4 space-y-1.5 text-left">
              {[["answered", "Answered"], ["staff_verified", "Verified"], ["not_applicable", "Not Applicable"], ["needs_followup", "Needs Follow-up"], ["pending_client", "Pending Client"], ["unknown", "Unknown"], ["blank", "Blank"]].map(([k, lbl]) => (
                <div key={k} className="flex items-center justify-between text-xs">
                  <span className="inline-flex items-center gap-1.5 text-slate-500">
                    <span className={cn("h-2 w-2 rounded-full", STATUS_META[k]?.dot || "bg-rose-400")} />{lbl}
                  </span>
                  <span className="font-semibold text-slate-700">{counts[k] || 0}</span>
                </div>
              ))}
            </div>
          </div>

          {/* next best actions */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5" data-testid="tp-nba">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-3">Next Best Actions</div>
            <div className="space-y-2.5">
              {(profile.next_best_actions || []).length === 0 && <div className="text-xs text-slate-400">No pending actions — profile looks healthy.</div>}
              {(profile.next_best_actions || []).map((a, i) => {
                const I = Icons[a.icon] || Icons.Circle;
                return (
                  <div key={i} className="flex items-start gap-2.5">
                    <span className="h-7 w-7 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center shrink-0"><I className="h-4 w-4" /></span>
                    <div><div className="text-sm font-medium text-[#171B2E]">{a.label}</div><div className="text-[11px] text-slate-400">Priority: {a.priority}</div></div>
                  </div>
                );
              })}
            </div>
            <button onClick={askAllison} disabled={askingAllison} data-testid="tp-ask-allison" className="mt-4 w-full inline-flex items-center justify-center gap-1.5 h-9 rounded-lg border border-fuchsia-200 bg-fuchsia-50 text-fuchsia-700 text-sm font-semibold hover:bg-fuchsia-100 disabled:opacity-60">
              {askingAllison ? <Icons.Loader2 className="h-4 w-4 animate-spin" /> : <Icons.Sparkles className="h-4 w-4" />}Ask Allison for Recommendations
            </button>
            {allison && (
              <div className="mt-3 rounded-xl bg-fuchsia-50/60 border border-fuchsia-100 p-3 text-xs text-slate-600 space-y-1.5" data-testid="tp-allison-result">
                <p className="whitespace-pre-wrap">{allison.narrative || allison.summary}</p>
                {(allison.risk_flags || []).length > 0 && (
                  <ul className="list-disc list-inside text-slate-500">{allison.risk_flags.slice(0, 4).map((f, i) => <li key={i}>{f}</li>)}</ul>
                )}
                <div className="text-[10px] text-fuchsia-400">{allison.source === "llm" ? "Generated by Allison (Claude)" : "Allison heuristic"}</div>
              </div>
            )}
          </div>

          {/* data sources */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5" data-testid="tp-sources">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-3">Data Sources</div>
            <div className="space-y-1.5">
              {Object.entries(SOURCE_META).map(([k, m]) => {
                const I = Icons[m.icon] || Icons.Circle;
                return (
                  <div key={k} className="flex items-center justify-between text-xs">
                    <span className="inline-flex items-center gap-1.5 text-slate-500"><I className="h-3.5 w-3.5" />{m.label}</span>
                    <span className="font-semibold text-slate-700">{profile.sources_count?.[k] || 0}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* last activity */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5" data-testid="tp-activity">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-3">Last Activity</div>
            <div className="space-y-3">
              {(profile.last_activity || []).length === 0 && <div className="text-xs text-slate-400">No changes recorded yet.</div>}
              {(profile.last_activity || []).map((h) => (
                <div key={h.id} className="text-xs">
                  <div className="text-slate-700"><span className="font-medium">{h.field_label}</span> → {h.new_value != null ? fmtVal(h.new_value) : cap(h.new_status)}</div>
                  <div className="text-[10px] text-slate-400">{fmtDT(h.created_at)} · {h.actor_name || cap(h.source)}</div>
                </div>
              ))}
            </div>
          </div>

          {/* P1 Intelligence Apply — admin only */}
          {isAdmin && (
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5" data-testid="tp-p1-panel">
              <div className="flex items-center gap-2 mb-1">
                <Icons.Sparkles className="h-4 w-4 text-fuchsia-500" />
                <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">P1 Intelligence</div>
              </div>
              <p className="text-[11px] text-slate-400 mb-3 leading-relaxed">
                Allison scans meeting transcripts and CRM notes to populate blank eligible fields — LOW-risk, ≥85% confidence, blank-only, no overwrites.
              </p>
              {p1Result && (
                <div className={cn(
                  "rounded-xl border px-3 py-2 mb-3 text-xs",
                  p1Result.applied_count > 0
                    ? "bg-emerald-50 border-emerald-200 text-emerald-700"
                    : "bg-slate-50 border-slate-200 text-slate-500"
                )} data-testid="tp-p1-result-summary">
                  {p1Result.applied_count > 0
                    ? <><span className="font-semibold">{p1Result.applied_count} field{p1Result.applied_count > 1 ? "s" : ""} applied</span> · {p1Result.protected_count} protected unchanged</>
                    : <span>No new eligible fields found · {p1Result.protected_count} protected unchanged</span>
                  }
                  <button onClick={() => setShowP1Dialog(true)} className="ml-2 text-blue-500 hover:underline text-[10px]" data-testid="tp-p1-view-details">View details</button>
                </div>
              )}
              <button
                onClick={runP1Apply}
                disabled={p1Running}
                data-testid="tp-p1-apply-btn"
                className="w-full inline-flex items-center justify-center gap-1.5 h-9 rounded-lg border border-fuchsia-200 bg-fuchsia-50 text-fuchsia-700 text-sm font-semibold hover:bg-fuchsia-100 disabled:opacity-60"
              >
                {p1Running
                  ? <><Icons.Loader2 className="h-4 w-4 animate-spin" />Applying Intelligence…</>
                  : <><Icons.Wand2 className="h-4 w-4" />Apply Intelligence</>
                }
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ── CIE Conflict Review — admin only ─────────────────────────── */}
      {isAdmin && (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5" data-testid="tp-cie-conflicts-panel">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Icons.ShieldAlert className="h-4 w-4 text-amber-500" />
              <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Intelligence Conflicts</div>
              {conflicts.length > 0 && (
                <span className="inline-flex items-center justify-center h-5 min-w-5 rounded-full bg-amber-100 text-amber-700 text-[10px] font-bold px-1.5" data-testid="tp-conflict-count">{conflicts.length}</span>
              )}
            </div>
            <button onClick={loadConflicts} disabled={conflictsLoading} className="text-[10px] text-slate-400 hover:text-slate-600" data-testid="tp-conflicts-refresh">
              {conflictsLoading ? <Icons.Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Icons.RefreshCw className="h-3.5 w-3.5" />}
            </button>
          </div>
          <p className="text-[11px] text-slate-400 mb-3 leading-relaxed">
            Evidence conflicts and HIGH-RISK / LEGAL fields that require staff review before the canonical value changes.
          </p>
          {conflictsLoading && <div className="flex justify-center py-3"><Icons.Loader2 className="h-5 w-5 animate-spin text-amber-400" /></div>}
          {!conflictsLoading && conflicts.length === 0 && (
            <div className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-2.5 text-xs text-slate-400 text-center" data-testid="tp-no-conflicts">
              No unresolved conflicts — all evidence is consistent.
            </div>
          )}
          {!conflictsLoading && conflicts.length > 0 && (
            <div className="space-y-3" data-testid="tp-conflict-list">
              {conflicts.map((c) => {
                const isHighLegal = ["high_risk", "legal_conclusion"].includes(c.review_reason);
                const isDeferred = c.decision_status === "deferred";
                const isStale = c.decision_status === "stale";
                const reviewBadge = c.review_reason === "high_risk"
                  ? { label: "HIGH-RISK", cls: "bg-orange-100 text-orange-700 border-orange-200" }
                  : c.review_reason === "legal_conclusion"
                    ? { label: "LEGAL", cls: "bg-rose-100 text-rose-700 border-rose-200" }
                    : { label: "CONFLICT", cls: "bg-amber-100 text-amber-700 border-amber-200" };
                const statusBadge = isDeferred
                  ? { label: "Deferred", cls: "bg-slate-100 text-slate-500" }
                  : isStale
                    ? { label: "Stale", cls: "bg-slate-100 text-slate-400" }
                    : { label: "Pending Review", cls: "bg-amber-50 text-amber-600" };
                const isBusy = conflictsBusy === c.id;

                return (
                  <div key={c.id} className={cn(
                    "rounded-xl border p-3 space-y-2",
                    isHighLegal ? "border-orange-200 bg-orange-50/30" : "border-slate-200 bg-white"
                  )} data-testid={`tp-conflict-${c.id}`}>
                    {/* header */}
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className={cn("text-[10px] font-semibold border rounded-full px-2 py-0.5", reviewBadge.cls)} data-testid={`tp-conflict-review-reason-${c.id}`}>{reviewBadge.label}</span>
                        <span className={cn("text-[10px] rounded-full px-2 py-0.5 font-medium", statusBadge.cls)} data-testid={`tp-conflict-status-${c.id}`}>{statusBadge.label}</span>
                      </div>
                      <span className="text-[10px] text-slate-400 shrink-0">{c.confidence != null ? `${Math.round(c.confidence * (c.confidence > 1 ? 1 : 100))}% conf.` : ""}</span>
                    </div>

                    {/* field label */}
                    <div className="text-sm font-semibold text-[#171B2E]" data-testid={`tp-conflict-field-${c.id}`}>{c.field_label || cap(c.field_name)}</div>

                    {/* values */}
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div className="rounded-lg border border-slate-100 bg-slate-50 px-2 py-1.5">
                        <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-0.5">Existing</div>
                        <div className="text-slate-700 font-medium truncate" data-testid={`tp-conflict-existing-${c.id}`}>{c.existing_value != null && c.existing_value !== "" ? String(c.existing_value) : <span className="text-slate-300 italic">blank</span>}</div>
                      </div>
                      <div className="rounded-lg border border-amber-100 bg-amber-50/40 px-2 py-1.5">
                        <div className="text-[10px] uppercase tracking-wide text-amber-400 mb-0.5">Proposed</div>
                        <div className="text-amber-700 font-semibold truncate" data-testid={`tp-conflict-proposed-${c.id}`}>{c.proposed_value != null ? String(c.proposed_value) : "—"}</div>
                      </div>
                    </div>

                    {/* source + excerpt */}
                    <div className="text-[10px] text-slate-400 space-y-0.5">
                      <div>Source: <span className="font-medium text-slate-500">{cap(c.source_type || c.source_channel || "unknown")}</span> · Captured: {new Date(c.created_at).toLocaleDateString()}</div>
                      {c.excerpt && <div className="italic truncate">&ldquo;{c.excerpt}&rdquo;</div>}
                    </div>

                    {/* HIGH/LEGAL governance warning */}
                    {isHighLegal && (
                      <div className="rounded-lg border border-orange-200 bg-orange-50 px-2.5 py-2 text-[10px] text-orange-700 flex items-start gap-1.5" data-testid={`tp-conflict-highlegal-warning-${c.id}`}>
                        <Icons.AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                        <span>Requires professional review and administrator authorization before acceptance.</span>
                      </div>
                    )}

                    {/* actions — hidden for already-resolved */}
                    {!isStale && (
                      <div className="flex items-center gap-2 pt-0.5">
                        <button
                          disabled={isBusy}
                          onClick={() => handleConflictAction(c, "accept")}
                          data-testid={`tp-conflict-accept-${c.id}`}
                          className="inline-flex items-center gap-1 h-7 px-2.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-[11px] font-semibold disabled:opacity-60"
                        >
                          {isBusy ? <Icons.Loader2 className="h-3 w-3 animate-spin" /> : <Icons.Check className="h-3 w-3" />}Accept
                        </button>
                        <button
                          disabled={isBusy}
                          onClick={() => handleConflictAction(c, "defer")}
                          data-testid={`tp-conflict-defer-${c.id}`}
                          className="inline-flex items-center gap-1 h-7 px-2.5 rounded-lg border border-slate-200 text-slate-600 text-[11px] font-semibold hover:bg-slate-50 disabled:opacity-60"
                        >
                          <Icons.Clock className="h-3 w-3" />Defer
                        </button>
                        <button
                          disabled={isBusy}
                          onClick={() => handleConflictAction(c, "reject")}
                          data-testid={`tp-conflict-reject-${c.id}`}
                          className="inline-flex items-center gap-1 h-7 px-2.5 rounded-lg border border-rose-200 text-rose-600 text-[11px] font-semibold hover:bg-rose-50 disabled:opacity-60"
                        >
                          <Icons.X className="h-3 w-3" />Reject
                        </button>
                      </div>
                    )}
                    {isStale && (
                      <div className="text-[10px] text-slate-400 italic" data-testid={`tp-conflict-stale-note-${c.id}`}>
                        Stale — canonical value changed after this conflict was stored. No action available.
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* missing items dialog */}
      <Dialog open={showMissing} onOpenChange={setShowMissing}>
        <DialogContent className="max-w-md" data-testid="tp-missing-dialog">
          <DialogHeader><DialogTitle>Missing / Needs Attention ({missing.length})</DialogTitle>
            <DialogDescription>Required and unresolved fields to reach full completion.</DialogDescription></DialogHeader>
          <div className="space-y-2 max-h-[60vh] overflow-y-auto">
            {missing.map((m) => (
              <div key={m.field_key} className="flex items-center justify-between border border-slate-100 rounded-lg px-3 py-2">
                <div><div className="text-sm text-[#171B2E]">{m.label}{m.required && <span className="text-rose-400 ml-1">*</span>}</div><div className="text-[10px] text-slate-400">{m.section}</div></div>
                <span className="text-[11px] text-slate-500">{cap(m.status)}</span>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* history dialog */}
      <Dialog open={showHistory} onOpenChange={setShowHistory}>
        <DialogContent className="max-w-lg" data-testid="tp-history-dialog">
          <DialogHeader><DialogTitle>Field History</DialogTitle>
            <DialogDescription>Chronological record of every change to this tax profile.</DialogDescription></DialogHeader>
          <div className="space-y-2.5 max-h-[60vh] overflow-y-auto">
            {history.length === 0 && <div className="text-sm text-slate-400">No history yet.</div>}
            {history.map((h) => (
              <div key={h.id} className="border border-slate-100 rounded-lg px-3 py-2 text-sm">
                <div className="font-medium text-[#171B2E]">{h.field_label}</div>
                <div className="text-xs text-slate-500">
                  {h.prev_value != null && h.prev_value !== "" && <span className="line-through text-slate-400">{fmtVal(h.prev_value)}</span>} {h.prev_value != null && h.prev_value !== "" && "→ "}
                  <span className="text-slate-700">{h.new_value != null ? fmtVal(h.new_value) : cap(h.new_status)}</span>
                </div>
                <div className="text-[10px] text-slate-400 mt-0.5">{fmtDT(h.created_at)} · {h.actor_name || "System"} · {cap(h.source)}</div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* P1 Intelligence Apply result dialog */}
      <Dialog open={showP1Dialog} onOpenChange={setShowP1Dialog}>
        <DialogContent className="max-w-2xl" data-testid="tp-p1-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Icons.Sparkles className="h-5 w-5 text-fuchsia-500" />
              P1 Intelligence Apply — Results
            </DialogTitle>
            <DialogDescription>
              Fields populated by Allison from existing intelligence sources. Only blank fields with LOW-risk classification and ≥85% confidence were applied. Protected and existing values were not changed.
            </DialogDescription>
          </DialogHeader>
          {p1Result && (
            <div className="space-y-4 max-h-[65vh] overflow-y-auto">
              {/* summary bar */}
              <div className="flex items-center gap-4 text-sm rounded-xl border border-slate-100 bg-slate-50 px-4 py-3">
                <div className="text-center">
                  <div className="text-xl font-bold text-fuchsia-600" data-testid="tp-p1-applied-count">{p1Result.applied_count}</div>
                  <div className="text-[10px] uppercase tracking-wide text-slate-400">Applied</div>
                </div>
                <div className="w-px h-8 bg-slate-200" />
                <div className="text-center">
                  <div className="text-xl font-bold text-slate-500" data-testid="tp-p1-protected-count">{p1Result.protected_count}</div>
                  <div className="text-[10px] uppercase tracking-wide text-slate-400">Protected</div>
                </div>
                <div className="w-px h-8 bg-slate-200" />
                <div className="flex-1 text-xs text-slate-500">
                  <span className="font-medium">Run:</span> {fmtDT(p1Result.executed_at)} ·{" "}
                  <span className="font-medium">Confidence threshold:</span> {p1Result.confidence_threshold}%
                </div>
              </div>

              {/* applied fields */}
              {p1Result.field_evidence?.length > 0 && (
                <div>
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-emerald-600 mb-2">Applied Fields</div>
                  <div className="space-y-2" data-testid="tp-p1-applied-fields">
                    {p1Result.field_evidence.map((e, i) => (
                      <div key={i} className="border border-emerald-100 rounded-xl px-3 py-2.5 bg-emerald-50/40" data-testid={`tp-p1-applied-${e.field}`}>
                        <div className="flex items-center justify-between gap-2 mb-1">
                          <span className="font-medium text-sm text-[#171B2E]">{e.label || cap(e.field)}</span>
                          <span className="text-[10px] bg-emerald-100 text-emerald-700 rounded-full px-2 py-0.5 font-medium">
                            {e.confidence}% · {cap(e.classification || e.field_status || "")}
                          </span>
                        </div>
                        <div className="text-xs text-slate-600 space-y-0.5">
                          <div>
                            <span className="text-slate-400 line-through">{e.before_value != null && e.before_value !== "" ? String(e.before_value) : "—"}</span>
                            {" → "}
                            <span className="font-semibold text-emerald-700">{e.after_value != null ? String(e.after_value) : "—"}</span>
                          </div>
                          {e.excerpt && <div className="italic text-[10px] text-slate-400">&ldquo;{e.excerpt}&rdquo;</div>}
                          <div className="text-[10px] text-slate-400">Source: {cap(e.source_type || "")} · Method: {cap(e.method || "")}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* no fields applied */}
              {p1Result.field_evidence?.length === 0 && (
                <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-500 text-center" data-testid="tp-p1-no-applied">
                  No blank eligible fields found. All qualifying fields are already populated.
                </div>
              )}

              {/* protected fields */}
              {p1Result.protected_evidence?.length > 0 && (
                <div>
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-2">Protected — Existing Values Preserved</div>
                  <div className="space-y-1.5" data-testid="tp-p1-protected-fields">
                    {p1Result.protected_evidence.map((p, i) => (
                      <div key={i} className="border border-slate-100 rounded-lg px-3 py-2 flex items-center justify-between text-xs" data-testid={`tp-p1-protected-${p.field}`}>
                        <span className="text-slate-600 font-medium">{cap(p.field)}</span>
                        <span className="text-slate-400 flex items-center gap-1">
                          <Icons.ShieldCheck className="h-3 w-3 text-fuchsia-400" />
                          {String(p.value)} · unchanged
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Professional Review Acknowledgment Modal — HIGH/LEGAL conflicts */}
      <Dialog open={!!ackModal} onOpenChange={(open) => { if (!open) setAckModal(null); }}>
        <DialogContent className="max-w-md" data-testid="tp-ack-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Icons.AlertTriangle className="h-5 w-5 text-orange-500" />
              Professional Review Required
            </DialogTitle>
            <DialogDescription>
              This conflict involves a {ackModal?.conflict?.review_reason === "legal_conclusion" ? "LEGAL-CONCLUSION" : "HIGH-RISK"} field.
              Acceptance requires confirmation that a qualified professional has reviewed this determination.
            </DialogDescription>
          </DialogHeader>
          {ackModal && (
            <div className="space-y-4">
              <div className="rounded-xl border border-orange-200 bg-orange-50 p-3 text-sm space-y-1.5">
                <div className="font-semibold text-[#171B2E]">{ackModal.conflict.field_label || cap(ackModal.conflict.field_name)}</div>
                <div className="text-xs text-slate-600">Proposed value: <span className="font-semibold text-amber-700">{String(ackModal.conflict.proposed_value ?? "—")}</span></div>
                <div className="text-xs text-slate-500">Source: {cap(ackModal.conflict.source_type || "unknown")} · Confidence: {ackModal.conflict.confidence != null ? `${Math.round(ackModal.conflict.confidence * (ackModal.conflict.confidence > 1 ? 1 : 100))}%` : "—"}</div>
              </div>
              <div className="text-sm text-slate-600">
                By accepting this conflict, you confirm that a qualified professional has reviewed this {ackModal.conflict.review_reason === "legal_conclusion" ? "legal determination" : "high-risk finding"} and authorized this value for the canonical client record.
              </div>
              <div className="flex items-center gap-3 pt-1">
                <button
                  onClick={() => decideConflict(ackModal.conflict, "accept", { professional_review_acknowledged: true })}
                  disabled={!!conflictsBusy}
                  data-testid="tp-ack-confirm-btn"
                  className="inline-flex items-center gap-1.5 h-9 px-4 rounded-lg bg-orange-600 hover:bg-orange-700 text-white text-sm font-semibold disabled:opacity-60"
                >
                  {conflictsBusy ? <Icons.Loader2 className="h-4 w-4 animate-spin" /> : <Icons.CheckCircle2 className="h-4 w-4" />}
                  Confirm &amp; Accept
                </button>
                <button
                  onClick={() => setAckModal(null)}
                  data-testid="tp-ack-cancel-btn"
                  className="inline-flex items-center h-9 px-4 rounded-lg border border-slate-200 text-sm text-slate-600 hover:bg-slate-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
